import * as React from 'react';
import { FC, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Dialog from 'components/dialog';
import { UserDetails } from 'state/auth/hooks/types';
import { getName } from 'utils/attributes';

import {
  CloseButton,
  ConfirmButton,
  DialogContent,
  DialogIconContainer,
  DialogTitle,
  ErrorIcon,
  ModalActionsContainer,
} from './styled';

type EligibilityErrorModalProps = {
  errorCode?: WalmartEligibilityErrors;
  onTryAgain: () => void;
  onDismiss: () => void;
  user?: UserDetails | null;
};

export enum WalmartEligibilityErrors {
  LOYALTY_ID_ALREADY_LINKED = 'LOYALTY_ID_ALREADY_LINKED',
  PARTNER_USER_ALREADY_LINKED = 'PARTNER_USER_ALREADY_LINKED',
  WRONG_TIER = 'WRONG_TIER',
  DEFAULT = 'DEFAULT',
}

export const EligibilityErrorModal: FC<React.PropsWithChildren<EligibilityErrorModalProps>> = ({
  errorCode,
  onTryAgain,
  onDismiss,
  user,
}) => {
  const { formatMessage } = useIntl();

  const modalMessageError = useMemo(() => {
    switch (errorCode) {
      case WalmartEligibilityErrors.LOYALTY_ID_ALREADY_LINKED:
        return formatMessage(
          { id: 'walmartMembershipAlreadyConnected' },
          { userEmail: user?.details.email }
        );
      case WalmartEligibilityErrors.PARTNER_USER_ALREADY_LINKED:
        const { firstName } = getName(
          { name: user?.details.name },
          { firstName: '', lastName: '' }
        );
        return formatMessage({ id: 'walmartAccountAlreadyConnected' }, { userName: firstName });
      case WalmartEligibilityErrors.WRONG_TIER:
        return formatMessage({ id: 'walmartAccountLinkingInterrupted' });
      default:
        return formatMessage({ id: 'walmartAccountLinkingInterrupted' });
    }
  }, [errorCode, formatMessage, user?.details.email, user?.details.name]);

  return (
    !!errorCode && (
      <Dialog
        showDialog
        headingComponent={
          <DialogTitle>{formatMessage({ id: 'walmartErrorLinkingAccounts' })}</DialogTitle>
        }
        bodyComponent={<DialogContent>{modalMessageError}</DialogContent>}
        onDismiss={onDismiss}
        modalAppearanceEventMessage="Walmart+ eligibility error modal"
        image={
          <DialogIconContainer>
            <ErrorIcon />
          </DialogIconContainer>
        }
        actions={
          <ModalActionsContainer>
            {[WalmartEligibilityErrors.DEFAULT, WalmartEligibilityErrors.WRONG_TIER].includes(
              errorCode
            ) ? (
              <>
                <ConfirmButton onPress={onTryAgain}>
                  {formatMessage({ id: 'tryAgain' })}
                </ConfirmButton>
                <CloseButton onPress={onDismiss}>{formatMessage({ id: 'close' })}</CloseButton>
              </>
            ) : (
              <ConfirmButton onPress={onDismiss}>{formatMessage({ id: 'ok' })}</ConfirmButton>
            )}
          </ModalActionsContainer>
        }
      />
    )
  );
};
